import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Users, Clock, FileText, Settings, Plus, Search, RefreshCw, Filter, GitBranch } from 'lucide-react';
import { FeaturedItinerary, MasterData } from '../types';
import { itineraryService } from '../services/itinerary';
import { masterDataService } from '../services/masterData';
import { itineraryVersionService } from '../services/itineraryVersionService';
import ItineraryFormModal from './ItineraryFormModal';
import PreviewModal from './PreviewModal';
import SetPriceModal from './SetPriceModal';
import VersionsModal from './modern/VersionsModal';
import FilterPanel from './modern/FilterPanel';
import { useToast } from './modern/ToastContainer';
import { useAuthContext } from '../contexts/AuthContext';
import { useThemeClasses } from '../utils/themeUtils';

interface BalifyItinerary {
  id: number;
  wyn_itinerary_id: string;
  itinerary_name: string;
  lead_guest_name: string;
  agency_name: string;
  travel_date: string;
  no_of_days: number;
  no_of_pax: number;
  no_of_children: number;
  status: string;
  quotation_number?: string;
  version_number?: number;
  has_pending_cancellation?: boolean;
}

const BalifyTablePage: React.FC = () => {
  const themeClasses = useThemeClasses();
  const { showToast } = useToast();
  const { user, canConvert, isAdmin } = useAuthContext();
  
  const [itineraries, setItineraries] = useState<BalifyItinerary[]>([]);
  const [masterData, setMasterData] = useState<MasterData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState<any>(null);
  
  // Modal states
  const [selectedItinerary, setSelectedItinerary] = useState<any | null>(null);
  const [action, setAction] = useState<'create' | 'edit' | 'copy' | null>(null);
  const [previewItinerary, setPreviewItinerary] = useState<any | null>(null);
  const [setPriceItinerary, setSetPriceItinerary] = useState<any | null>(null);
  const [statusChangeConfirm, setStatusChangeConfirm] = useState<{id: number, newStatus: string, currentStatus: string} | null>(null);
  const [versionsItineraryId, setVersionsItineraryId] = useState<number | null>(null);

  const handleCreateVersion = (itinerary: BalifyItinerary) => {
    setVersionsItineraryId(itinerary.id);
  };

  useEffect(() => {
    loadData();
    loadMasterData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await itineraryService.getRecentItineraries(100, 0);
      setItineraries(response || []);
    } catch (error) {
      console.error('Error loading itineraries:', error);
      setItineraries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      loadData();
      return;
    }

    try {
      setLoading(true);
      setSearchQuery(query);
      
      const results = await itineraryService.searchItineraries({ q: query });
      
      setItineraries(results || []);
      setActiveFilters(null);
    } catch (error) {
      console.error('Error searching:', error);
      setItineraries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterApply = async (filters: any) => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (filters.status) params.status = filters.status;
      if (filters.dateFrom) params.date_from = filters.dateFrom;
      if (filters.dateTo) params.date_to = filters.dateTo;
      if (filters.agencyName) params.agency_name = filters.agencyName;
      
      // Advanced pax filtering
      if (filters.paxOperator === 'between' && filters.paxMin && filters.paxMax) {
        params.pax_operator = 'between';
        params.pax_min = parseInt(filters.paxMin);
        params.pax_max = parseInt(filters.paxMax);
      } else if (filters.noOfPax && filters.paxOperator) {
        params.pax_operator = filters.paxOperator;
        params.no_of_pax = parseInt(filters.noOfPax);
      }
      
      // Advanced children filtering
      if (filters.childrenOperator === 'between' && filters.childrenMin && filters.childrenMax) {
        params.children_operator = 'between';
        params.children_min = parseInt(filters.childrenMin);
        params.children_max = parseInt(filters.childrenMax);
      } else if (filters.noOfChildren && filters.childrenOperator) {
        params.children_operator = filters.childrenOperator;
        params.no_of_children = parseInt(filters.noOfChildren);
      }
      
      const results = await itineraryService.searchItineraries(params);
      setItineraries(results || []);
      setActiveFilters(filters);
      setSearchQuery('');
    } catch (error) {
      console.error('Error filtering:', error);
      setItineraries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMasterData = async () => {
    try {
      const master = await masterDataService.getMasterData();
      setMasterData(master);
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  };

  const getFilteredItineraries = (status: string) => {
    let filtered = itineraries;
    
    if (status === 'TENTATIVE') {
      filtered = filtered.filter(i => i.status === 'TENTATIVE' || i.status === 'TENTATIVE_BOOKING');
    } else if (status !== 'all') {
      filtered = filtered.filter(i => i.status === status);
    }
    
    return filtered;
  };

  const getFilterSummary = () => {
    if (!activeFilters) return '';
    const parts = [];
    if (activeFilters.dateFrom && activeFilters.dateTo) {
      parts.push(`Trips between ${activeFilters.dateFrom} and ${activeFilters.dateTo}`);
    } else if (activeFilters.dateFrom) {
      parts.push(`Trips from ${activeFilters.dateFrom}`);
    } else if (activeFilters.dateTo) {
      parts.push(`Trips until ${activeFilters.dateTo}`);
    }
    if (activeFilters.status) parts.push(`Status: ${activeFilters.status}`);
    if (activeFilters.noOfPax) parts.push(`${activeFilters.noOfPax} Adults`);
    if (activeFilters.noOfChildren) parts.push(`${activeFilters.noOfChildren} Children`);
    if (activeFilters.agencyName) parts.push(`Agency: ${activeFilters.agencyName}`);
    return parts.join(' • ');
  };

  const statusLabels: Record<string, string> = {
    PENDING_CONFIRMATION: 'Pending Confirmation',
    TENTATIVE: 'Tentative',
    TENTATIVE_BOOKING: 'Tentative',
    CONVERTED: 'Converted',
    PENDING_CANCELLATION: 'Pending Cancellation',
    CANCELED: 'Canceled',
    AGENT_CONFIRMED: 'Agent Confirmed',
    ARCHIVED: 'Archived',
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONVERTED': return 'bg-green-100 text-green-800';
      case 'TENTATIVE': return 'bg-yellow-100 text-yellow-800';
      case 'TENTATIVE_BOOKING': return 'bg-yellow-100 text-yellow-800';
      case 'PENDING_CONFIRMATION': return 'bg-blue-100 text-blue-800';
      case 'PENDING_CANCELLATION': return 'bg-orange-100 text-orange-800';
      case 'CANCELED': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreate = () => {
    setSelectedItinerary(null);
    setAction('create');
  };

  const handleEdit = async (itinerary: BalifyItinerary) => {
    try {
      const itin = await itineraryService.getItinerary(itinerary.id);
      setSelectedItinerary(itin);
      setAction('edit');
    } catch (error) {
      console.error('Error loading itinerary:', error);
      showToast('Failed to load itinerary', 'error');
    }
  };

  const handleCopy = async (itinerary: BalifyItinerary) => {
    try {
      const itin = await itineraryService.getItinerary(itinerary.id);
      itin.itinerary_id = -1;
      itin.basic_info.agency_id = -1;
      itin.basic_info.itinerary_name = '';
      itin.basic_info.lead_guest = '';
      itin.status = 'PENDING_CONFIRMATION';
      setSelectedItinerary(itin);
      setAction('copy');
    } catch (error) {
      console.error('Error copying itinerary:', error);
      showToast('Failed to copy itinerary', 'error');
    }
  };

  const handlePreview = async (itinerary: BalifyItinerary) => {
    try {
      const itin = await itineraryService.getItinerary(itinerary.id);
      setPreviewItinerary(itin);
    } catch (error) {
      console.error('Error loading itinerary for preview:', error);
      showToast('Failed to load preview', 'error');
    }
  };

  const handleDownload = async (itinerary: BalifyItinerary, isAgent = false) => {
    try {
      console.log('🔍 Download attempt for itinerary:', itinerary.id, 'isAgent:', isAgent);
      
      // Check if itinerary has set price
      const fullItinerary = await itineraryService.getItinerary(itinerary.id);
      console.log('📋 Full itinerary loaded:', fullItinerary?.id);
      
      const givenPrice = fullItinerary?.given_price;
      console.log('💰 Given price data:', givenPrice);
      
      let hasSetPrice = false;
      
      if (givenPrice && typeof givenPrice === 'object') {
        // Check if any currency has a total > 0
        hasSetPrice = Object.values(givenPrice).some((priceData: any) => {
          const hasPrice = priceData && typeof priceData === 'object' && priceData.total && priceData.total > 0;
          console.log('💵 Price check for currency:', priceData, 'hasPrice:', hasPrice);
          return hasPrice;
        });
      }
      
      console.log('✅ Final price validation result:', hasSetPrice);
      
      if (!hasSetPrice) {
        console.log('❌ Price not set - blocking download');
        showToast('Please set price before downloading', 'error');
        return;
      }
      
      console.log('🚀 Proceeding with download');
      
      if (isAgent) {
        await itineraryService.downloadAgentItinerary(itinerary.id);
      } else {
        await itineraryService.downloadItinerary(itinerary.id, false);
      }
      
      showToast('Download started', 'success');
    } catch (error: any) {
      console.error('Error downloading:', error);
      showToast(error.message || 'Failed to download', 'error');
    }
  };

  const handleSetPrice = async (itinerary: BalifyItinerary) => {
    try {
      const itin = await itineraryService.getItinerary(itinerary.id);
      setSetPriceItinerary(itin);
    } catch (error) {
      console.error('Error loading itinerary for pricing:', error);
      showToast('Failed to load itinerary', 'error');
    }
  };

  const handleStatusChange = async (id: number, newStatus: string, currentStatus: string) => {
    setStatusChangeConfirm({ id, newStatus, currentStatus });
  };

  const confirmStatusChange = async () => {
    if (!statusChangeConfirm) return;
    
    const { id, newStatus, currentStatus } = statusChangeConfirm;
    setStatusChangeConfirm(null);
    
    try {
      let result;
      if (newStatus === 'CONVERTED') {
        result = await itineraryService.convertItinerary(id);
      } else {
        result = await itineraryService.updateStatus(id, newStatus);
      }
      
      showToast('Status updated successfully', 'success');
      loadData();
    } catch (error: any) {
      console.error('Error updating status:', error);
      showToast(error?.response?.data?.detail || 'Failed to update status', 'error');
      loadData();
    }
  };

  const handleFormClose = () => {
    setSelectedItinerary(null);
    setAction(null);
    loadData();
  };

  const allItineraries = getFilteredItineraries('all');
  const pendingItineraries = getFilteredItineraries('PENDING_CONFIRMATION');
  const tentativeItineraries = getFilteredItineraries('TENTATIVE');
  const convertedItineraries = getFilteredItineraries('CONVERTED');
  const pendingCancellationItineraries = getFilteredItineraries('PENDING_CANCELLATION');
  const canceledItineraries = getFilteredItineraries('CANCELED');

  const tabs = [
    { id: 'all', label: `All (${allItineraries.length})`, data: allItineraries },
    { id: 'pending', label: `Pending (${pendingItineraries.length})`, data: pendingItineraries },
    { id: 'tentative', label: `Tentative (${tentativeItineraries.length})`, data: tentativeItineraries },
    { id: 'converted', label: `Converted (${convertedItineraries.length})`, data: convertedItineraries },
    ...(pendingCancellationItineraries.length > 0 ? [{ id: 'pending_cancellation', label: `Pending Cancellation (${pendingCancellationItineraries.length})`, data: pendingCancellationItineraries }] : []),
    ...(canceledItineraries.length > 0 ? [{ id: 'canceled', label: `Canceled (${canceledItineraries.length})`, data: canceledItineraries }] : []),
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab)?.data || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col ${themeClasses.isModern ? 'bg-gray-50' : 'bg-gray-100'}`}>
      <div className="w-full px-3 sm:px-6 lg:px-8 py-3 sm:py-6 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Balify</h1>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">Manage and view all itineraries</p>
            </div>
            <button
              onClick={handleCreate}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">New Itinerary</span>
              <span className="sm:hidden">New</span>
            </button>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:gap-4">
          {/* Search */}
          <div className="w-full">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <input
                  type="text"
                  placeholder="Search itineraries..."
                  value={activeFilters ? getFilterSummary() : searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch(searchQuery)}
                  className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 bg-white border border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                />
              </div>
              <button
                onClick={() => handleSearch(searchQuery)}
                className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all font-medium whitespace-nowrap text-xs sm:text-sm"
              >
                Search
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowFilters(true)}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-400 text-white border border-blue-500 rounded-lg hover:bg-blue-500 transition-all flex items-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              <Filter className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Filter</span>
            </button>

            <button
              onClick={loadData}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-blue-400 text-white rounded-lg hover:bg-blue-500 transition-colors"
            >
              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            <button
              onClick={handleCreate}
              className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium sm:hidden"
            >
              <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
              New
            </button>
          </div>
        </div>

        <div className={`${themeClasses.card} border`}>
          {/* Tabs */}
          <div className={themeClasses.tabs.container}>
            <nav className="-mb-px flex space-x-2 sm:space-x-4 lg:space-x-8 px-2 sm:px-3 lg:px-6 overflow-x-auto" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-3 sm:py-4 px-1 border-b-2 font-medium text-xs sm:text-sm ${
                    activeTab === tab.id
                      ? themeClasses.tabs.active.replace('bg-white', '').replace('bg-gray-50', '')
                      : themeClasses.tabs.inactive.replace('hover:bg-gray-100', 'hover:border-gray-300')
                  }`}
                >
                  <span className="sm:hidden">
                    {tab.label.split(' ')[0]} ({tab.data.length})
                  </span>
                  <span className="hidden sm:inline">
                    {tab.label}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Mobile Card View (hidden on desktop) */}
          <div className="block sm:hidden">
            <div className="p-2 space-y-2">
              {activeTabData.map((itinerary) => (
                <div key={itinerary.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center min-w-0 flex-1">
                      <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          {itinerary.quotation_number ? (
                            <span className="font-mono font-bold text-blue-700 cursor-pointer hover:text-blue-900" onClick={() => setVersionsItineraryId(itinerary.id)}>{itinerary.quotation_number}/{itinerary.version_number}</span>
                          ) : (
                            itinerary.wyn_itinerary_id || `WH_${itinerary.id}`
                          )}
                          {itinerary.quotation_number && (
                            <span className="ml-1.5 text-[10px] text-gray-400 font-normal">{itinerary.wyn_itinerary_id || `WH_${itinerary.id}`}</span>
                          )}
                          {itinerary.has_pending_cancellation && (
                            <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded">🚨 Cancel Bookings</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 truncate" title={itinerary.itinerary_name || 'Untitled'}>
                          {itinerary.itinerary_name || 'Untitled'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button 
                        className="text-blue-600 hover:text-blue-900 p-1 rounded" 
                        title="Edit Itinerary"
                        onClick={() => handleEdit(itinerary)}
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                    <div>
                      <div className="text-gray-500">Guest:</div>
                      <div className="font-medium truncate" title={itinerary.lead_guest_name || 'No guest'}>
                        {itinerary.lead_guest_name || 'No guest'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Agency:</div>
                      <div className="font-medium truncate" title={itinerary.agency_name || 'No agency'}>
                        {itinerary.agency_name || 'No agency'}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Travel Date:</div>
                      <div className="font-medium">
                        {itinerary.travel_date ? 
                          new Date(itinerary.travel_date).toLocaleDateString('en-GB') : 
                          'No date'
                        }
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-500">Duration & Pax:</div>
                      <div className="font-medium">
                        {itinerary.no_of_days || 0}d • {itinerary.no_of_pax || 0}p
                        {(itinerary.no_of_children || 0) > 0 && ` +${itinerary.no_of_children}c`}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    {itinerary.status === 'CANCELED' || (['CONVERTED'].includes(itinerary.status) && !canConvert) ? (
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(itinerary.status)}`}>
                        {statusLabels[itinerary.status] || itinerary.status.replace(/_/g, ' ')}
                      </span>
                    ) : ['CONVERTED', 'PENDING_CANCELLATION'].includes(itinerary.status) ? (
                      <select
                        value={itinerary.status}
                        onChange={(e) => handleStatusChange(itinerary.id, e.target.value, itinerary.status)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer ${getStatusColor(itinerary.status)}`}
                      >
                        <option value={itinerary.status}>{statusLabels[itinerary.status] || itinerary.status.replace(/_/g, ' ')}</option>
                        {canConvert && itinerary.status !== 'PENDING_CANCELLATION' && <option value="PENDING_CANCELLATION">Pending Cancellation</option>}
                        {isAdmin && <option value="CANCELED">Canceled</option>}
                      </select>
                    ) : (
                      <select
                        value={itinerary.status}
                        onChange={(e) => handleStatusChange(itinerary.id, e.target.value, itinerary.status)}
                        className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer ${getStatusColor(itinerary.status)}`}
                      >
                        <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                        <option value="TENTATIVE">Tentative</option>
                        {canConvert && <option value="CONVERTED">Converted</option>}
                      </select>
                    )}
                    <div className="flex items-center space-x-1">
                      <button 
                        className="text-green-600 hover:text-green-900 p-1 rounded" 
                        title="Preview"
                        onClick={() => handlePreview(itinerary)}
                      >
                        👁️
                      </button>
                      <button 
                        className="text-purple-600 hover:text-purple-900 p-1 rounded" 
                        title="Copy"
                        onClick={() => handleCopy(itinerary)}
                      >
                        📋
                      </button>
                      <button 
                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded disabled:opacity-50" 
                        title="Create Version"
                        
                        onClick={() => handleCreateVersion(itinerary)}
                      >
                        <GitBranch className="w-4 h-4" />
                      </button>
                      <button 
                        className="text-orange-600 hover:text-orange-900 p-1 rounded" 
                        title="Set Price"
                        onClick={() => handleSetPrice(itinerary)}
                      >
                        💰
                      </button>
                      <button 
                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded" 
                        title="Download"
                        onClick={() => handleDownload(itinerary)}
                      >
                        📥
                      </button>
                      <button 
                        className="text-indigo-600 hover:text-indigo-900 p-1 rounded" 
                        title="Agent Download"
                        onClick={() => handleDownload(itinerary, true)}
                      >
                        📄
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Table View (hidden on mobile) */}
          <div className="hidden sm:block">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Itinerary Details
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Guest & Agency
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                      Travel Info
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeTabData.map((itinerary) => (
                    <tr key={itinerary.id} className="hover:bg-gray-50">
                      <td className="px-3 py-4">
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {itinerary.quotation_number ? (
                                <span className="font-mono font-bold text-blue-700 cursor-pointer hover:text-blue-900" onClick={() => setVersionsItineraryId(itinerary.id)}>{itinerary.quotation_number}/{itinerary.version_number}</span>
                              ) : (
                                itinerary.wyn_itinerary_id || `WH_${itinerary.id}`
                              )}
                              {itinerary.quotation_number && (
                                <span className="ml-1.5 text-[10px] text-gray-400 font-normal">{itinerary.wyn_itinerary_id || `WH_${itinerary.id}`}</span>
                              )}
                              {itinerary.has_pending_cancellation && (
                                <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded" title="Active bookings need cancellation">🚨 Cancel Bookings</span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500 truncate" title={itinerary.itinerary_name || 'Untitled'}>
                              {(itinerary.itinerary_name || 'Untitled').length > 25 
                                ? `${(itinerary.itinerary_name || 'Untitled').substring(0, 25)}...` 
                                : (itinerary.itinerary_name || 'Untitled')
                              }
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-3 py-4">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate" title={itinerary.lead_guest_name || 'No guest'}>
                            {(itinerary.lead_guest_name || 'No guest').length > 20 
                              ? `${(itinerary.lead_guest_name || 'No guest').substring(0, 20)}...` 
                              : (itinerary.lead_guest_name || 'No guest')
                            }
                          </div>
                          <div className="text-sm text-gray-500 truncate" title={itinerary.agency_name || 'No agency'}>
                            {(itinerary.agency_name || 'No agency').length > 20 
                              ? `${(itinerary.agency_name || 'No agency').substring(0, 20)}...` 
                              : (itinerary.agency_name || 'No agency')
                            }
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-3 py-4">
                        <div className="text-sm text-gray-900 flex items-center">
                          <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">
                            {itinerary.travel_date ? 
                              new Date(itinerary.travel_date).toLocaleDateString('en-GB') : 
                              'No date'
                            }
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 mt-1 flex items-center">
                          <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span>{itinerary.no_of_days || 0}d</span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center">
                          <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span>
                            {itinerary.no_of_pax || 0}p
                            {(itinerary.no_of_children || 0) > 0 && 
                              ` +${itinerary.no_of_children}c`
                            }
                          </span>
                        </div>
                      </td>

                      <td className="px-3 py-4">
                        {itinerary.status === 'CANCELED' || (['CONVERTED'].includes(itinerary.status) && !canConvert) ? (
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(itinerary.status)}`}>
                            {statusLabels[itinerary.status] || itinerary.status.replace(/_/g, ' ')}
                          </span>
                        ) : ['CONVERTED', 'PENDING_CANCELLATION'].includes(itinerary.status) ? (
                          <select
                            value={itinerary.status}
                            onChange={(e) => handleStatusChange(itinerary.id, e.target.value, itinerary.status)}
                            className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer ${getStatusColor(itinerary.status)}`}
                          >
                            <option value={itinerary.status}>{statusLabels[itinerary.status] || itinerary.status.replace(/_/g, ' ')}</option>
                            {canConvert && itinerary.status !== 'PENDING_CANCELLATION' && <option value="PENDING_CANCELLATION">Pending Cancellation</option>}
                            {isAdmin && <option value="CANCELED">Canceled</option>}
                          </select>
                        ) : (
                          <select
                            value={itinerary.status}
                            onChange={(e) => handleStatusChange(itinerary.id, e.target.value, itinerary.status)}
                            className={`px-2 py-1 rounded-full text-xs font-medium border cursor-pointer ${getStatusColor(itinerary.status)}`}
                          >
                            <option value="PENDING_CONFIRMATION">Pending Confirmation</option>
                            <option value="TENTATIVE">Tentative</option>
                            {canConvert && <option value="CONVERTED">Converted</option>}
                          </select>
                        )}
                      </td>
                      
                      <td className="px-3 py-4 text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button 
                            className="text-blue-600 hover:text-blue-900 p-1 rounded" 
                            title="Edit Itinerary"
                            onClick={() => handleEdit(itinerary)}
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                          <button 
                            className="text-green-600 hover:text-green-900 p-1 rounded" 
                            title="Preview"
                            onClick={() => handlePreview(itinerary)}
                          >
                            👁️
                          </button>
                          <button 
                            className="text-purple-600 hover:text-purple-900 p-1 rounded" 
                            title="Copy"
                            onClick={() => handleCopy(itinerary)}
                          >
                            📋
                          </button>
                          <button 
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded disabled:opacity-50" 
                            title="Create Version"
                            
                            onClick={() => handleCreateVersion(itinerary)}
                          >
                            <GitBranch className="w-4 h-4" />
                          </button>
                          <button 
                            className="text-orange-600 hover:text-orange-900 p-1 rounded" 
                            title="Set Price"
                            onClick={() => handleSetPrice(itinerary)}
                          >
                            💰
                          </button>
                          <button 
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded" 
                            title="Download"
                            onClick={() => handleDownload(itinerary)}
                          >
                            📥
                          </button>
                          <button 
                            className="text-indigo-600 hover:text-indigo-900 p-1 rounded" 
                            title="Agent Download"
                            onClick={() => handleDownload(itinerary, true)}
                          >
                            📄
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {activeTabData.length === 0 && (
            <div className="text-center py-8 sm:py-12 px-4">
              <FileText className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
              <p className="text-gray-500 text-sm">No itineraries found in this category.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Modals */}
      {action && masterData && (
        <ItineraryFormModal
          itinerary={selectedItinerary}
          action={action}
          masterData={masterData}
          onClose={handleFormClose}
        />
      )}

      {previewItinerary && (
        <PreviewModal
          data={previewItinerary}
          onClose={() => setPreviewItinerary(null)}
        />
      )}

      {setPriceItinerary && (
        <SetPriceModal
          itinerary={setPriceItinerary}
          onClose={() => setSetPriceItinerary(null)}
          onSuccess={() => {
            setSetPriceItinerary(null);
            showToast('Price updated successfully', 'success');
            loadData();
          }}
        />
      )}
      {versionsItineraryId && (
        <VersionsModal
          itineraryId={versionsItineraryId}
          onClose={() => setVersionsItineraryId(null)}
          onCreateVersion={(id) => {
            setVersionsItineraryId(null);
            window.location.href = `/balify?version_of=${id}`;
          }}
        />
      )}
      {/* Filter Panel */}
      <FilterPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={handleFilterApply}
        onReset={() => {
          loadData();
          setActiveFilters(null);
          setSearchQuery('');
        }}
      />

      {/* Status Change Confirmation Dialog */}
      {statusChangeConfirm && (() => {
        const statusLabels: Record<string, string> = {
          PENDING_CONFIRMATION: 'Pending Confirmation',
          TENTATIVE: 'Tentative',
          TENTATIVE_BOOKING: 'Tentative',
          CONVERTED: 'Converted',
          PENDING_CANCELLATION: 'Pending Cancellation',
          CANCELED: 'Canceled'
        };
        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Change Status</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Change status from <strong>{statusLabels[statusChangeConfirm.currentStatus]}</strong> to <strong>{statusLabels[statusChangeConfirm.newStatus]}</strong>?
                  </p>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => setStatusChangeConfirm(null)} 
                      className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={confirmStatusChange} 
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default BalifyTablePage;